import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    DOCUMENT,
    ElementRef,
    inject,
    input,
    model,
    Renderer2,
    viewChild,
} from '@angular/core';

import { ButtonIcon } from 'app/shared/buttons';

@Component({
    selector: 'stby-modal-base',
    imports: [ButtonIcon],
    templateUrl: './modal-base.html',
    styleUrl: './modal-base.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:keydown.escape)': 'onDocumentEscape($event)',
    },
})
export class ModalBase {
    private document = inject(DOCUMENT);
    private renderer = inject(Renderer2);
    private destroyRef = inject(DestroyRef);

    private dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

    visible = model<boolean>(false);
    preventLightDismiss = input<boolean>(false);
    hideCloseButton = input<boolean>(false);
    beforeClose = input<(close: () => void) => void>();

    constructor() {
        afterRenderEffect(() => {
            const dialogElement = this.dialog().nativeElement;

            if (this.visible()) {
                if (!dialogElement.open) {
                    dialogElement.showModal();
                    this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
                }
            } else {
                if (dialogElement.open) {
                    dialogElement.close();
                }
                this.renderer.removeStyle(this.document.body, 'overflow');
            }
        });

        this.destroyRef.onDestroy(() => {
            this.renderer.removeStyle(this.document.body, 'overflow');
        });
    }

    backdropClick(event: MouseEvent) {
        if (!this.preventLightDismiss() && event.target instanceof HTMLDialogElement) {
            this.requestClose();
        }
    }

    onDocumentEscape(event: Event) {
        if (!this.dialog().nativeElement.open) return;

        if (this.preventLightDismiss()) {
            event.preventDefault();
            return;
        }

        if (this.beforeClose()) {
            event.preventDefault();
            this.requestClose();
        }
    }

    requestClose() {
        const beforeClose = this.beforeClose();

        if (beforeClose) {
            beforeClose(() => this.close());
        } else {
            this.close();
        }
    }

    open() {
        this.visible.set(true);
    }

    close() {
        this.visible.set(false);
    }
}
